/**
 * THE WHITE SCREEN, piece 2 (Bobbie, 2026-08-22): register the app-shell
 * caching worker for EVERY visitor, not only the ones who opt into push.
 *
 * hub/public/firebase-messaging-sw.js does two jobs now: push (unchanged)
 * and app-shell caching (new - precache/serve the hashed asset bundle and
 * the shell HTML so a repeat visit paints from disk, even offline). Push
 * only registers that worker when someone explicitly enables notifications
 * (src/lib/push.ts's enablePush/refreshPushToken), which would leave most
 * families - anyone who never opted into push - without the caching
 * benefit THE WHITE SCREEN packet exists to deliver to everyone. This
 * function closes that gap by registering the SAME worker unconditionally,
 * on every page load, just after the first paint (see the timing note in
 * registerAppShellWorker below).
 *
 * Registering a service worker does not itself prompt for anything - only
 * Notification.requestPermission() (called elsewhere, only from an explicit
 * "enable push" tap) shows a permission dialog. So calling this on every
 * load is invisible to someone who never touches push.
 *
 * MUST reuse messagingWorker.ts's registerMessagingWorker(), the same
 * function push.ts uses, not a second copy of the URL-building logic - see
 * the long comment on that function for exactly why a second,
 * differently-built registration URL would silently break push for some
 * devices.
 */
// STATIC, and from messagingWorker.ts rather than push.ts (PR #1242 refute
// round). push.ts pulls firebase/messaging and is a lazy chunk; reaching
// the registration through it meant a push chunk that failed to load also
// skipped this worker for the visit. messagingWorker.ts imports nothing.
import { registerMessagingWorker } from './messagingWorker';

let started = false;

/**
 * How long after the first painted frame the registration stops waiting for
 * window `load` (PR #1242 round two refute, P3). Exported so
 * tests/harness/app-shell-worker/check.mjs times the same number.
 *
 * Two seconds is a judgment from both ends, not a measurement:
 *   - Not much shorter. By the time main.tsx calls this, the entry and every
 *     chunk it imports statically have already downloaded (this call is in
 *     that code), so what is left of the first load is the route chunk and
 *     the first answers from Auth and Firestore. Two seconds gives those a
 *     head start over the worker's own downloads, which is the whole point
 *     of deferring at all.
 *   - Not longer. This worker is the white-screen protection from #1300: a
 *     first visit that never registers it leaves nothing on disk for the
 *     next one. Someone on a weak signal who opens the Hub and gives up
 *     within a few seconds should still leave with it, and reCAPTCHA can
 *     hold `load` open for as long as it likes.
 * When `load` comes first, as it did in the harness whenever nothing held
 * it, this timer does nothing.
 */
export const APP_SHELL_WORKER_FALLBACK_MS = 2000;

/**
 * Fire-and-forget. Never throws: a failed registration (unsupported
 * browser, blocked storage, a flaky network mid-install) must degrade to
 * "no caching this visit", never to a broken app boot.
 */
export function registerAppShellWorker(): void {
  if (started) return;
  started = true;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // DEFERRED PAST FIRST PAINT (Codex review of PR #1242, P2). main.tsx
  // calls this at module scope, BEFORE ReactDOM.createRoot(...).render(), so
  // registering here directly would start the worker's own downloads during
  // startup: its script, the two Firebase compat scripts it importScripts()
  // from gstatic, and the shell fetch in its install handler, all competing
  // with the Hub's own first load on exactly the slow phones this is for.
  //
  // But NOT "until load" alone (round two refute, P3). Waiting for `load`
  // is not free. With App Check on, the SDK adds Google's reCAPTCHA script
  // and frame to the page, and `load` waits for them. Measured in Chromium
  // with a fake site key, waiting for `load` alone: a reCAPTCHA that never
  // answered meant this worker was never registered in 20 seconds, and one
  // 8 seconds slow put it at about 8.2 seconds, where hub-staging (which
  // registered at once) took about 170 ms. tests/harness/app-shell-worker
  // pins both cases. So the worker registers at whichever comes FIRST:
  //   - `load`, plus a macrotask, or
  //   - APP_SHELL_WORKER_FALLBACK_MS after the first painted frame,
  // and both wait for that first frame. requestAnimationFrame runs just
  // before a frame is painted, so a timer set inside it runs after the
  // paint. Not requestIdleCallback: Safari does not ship it, and an idle
  // main thread says nothing about an idle network, which is the contention
  // that matters here. A tab opened in the background paints no frame and
  // runs no animation frames, so it registers once it is first shown; no
  // one is waiting on a page nobody has looked at yet.
  //
  // `fired` keeps it to exactly one registration per page load, whichever
  // path gets there first.
  let fired = false;
  const start = (): void => {
    if (fired) return;
    fired = true;
    void registerMessagingWorker().catch(() => {
      // Best effort. push.ts's own enablePush flow surfaces a real error to
      // the person when registration matters to THEM; here it only means one
      // visitor's device does not get the instant-repeat-open benefit yet.
    });
  };

  const afterFirstFrame = (): void => {
    window.setTimeout(start, APP_SHELL_WORKER_FALLBACK_MS);
    if (document.readyState === 'complete') {
      window.setTimeout(start, 0);
    } else {
      window.addEventListener('load', () => window.setTimeout(start, 0), { once: true });
    }
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => window.setTimeout(afterFirstFrame, 0));
  } else {
    // No animation frames at all (a very old engine): the load-or-timer race
    // alone, counted from here. This module is our own code running, so the
    // shell in index.html has already been parsed.
    afterFirstFrame();
  }
}
